<template>
  <page-template :is-loading="isLoading" :data-exists="unionUsersData !== null">
    <template #pageHeadline>
      {{ $t('usersManagement.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="unionUsersData"
        :sticky-header="true"
        :row-buttons="rowButtons"
        :row-classes="
          (item) => {
            return newUserId === item.id ? 'highlight-row' : '';
          }
        "
        :show-download-button="false"
        data-qa="users-table"
        :show-filter-row="true"
        @edit-row-button-clicked="openEditUserModal($event as UserDetails)"
        @delete-row-button-clicked="openDeleteUserModal($event as UserDetails)"
        @new-button-clicked="openAddNewUserModal"
      >
      </rbz-table>
      <rbz-modal-dialog
        :show="userForm !== null"
        :title="
          userForm === 'new'
            ? $t('usersManagement.modal.newTitle')
            : $t('usersManagement.modal.editTitle')
        "
        :primary-btn-title="$t('common.operations.save.modal.confirm')"
        :primary-btn-disabled="!isUserFormValid"
        @onPrimaryBtnClick="saveUser"
        :secondary-btn-title="$t('common.operations.save.modal.cancel')"
        @onSecondaryBtnClick="userForm = null"
        @closeModal="userForm = null"
        size="small"
      >
        <template #content="{ container }">
          <div class="rbz-field">
            <rbz-form-input
              name="contact-name-input"
              data-qa="contact-name-input"
              :label="$t('usersManagement.modal.contactName')"
              :placeholder="$t('usersManagement.modal.contactName')"
              :is-invalid="!isNameValid"
              v-model:inputValue="user.contact_name"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="email-input"
              data-qa="email-input"
              :label="$t('usersManagement.email')"
              :placeholder="$t('usersManagement.email')"
              :disabled="userForm === 'edit'"
              :is-invalid="!isEmailValid"
              v-model:inputValue="user.email"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="mobile-input"
              data-qa="mobile-input"
              :label="$t('usersManagement.mobile')"
              :placeholder="$t('usersManagement.mobile')"
              :is-invalid="!isMobileValid"
              v-model:inputValue="user.mobile"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              :items="
                accessLevels
                  .filter((item) => Number(item.value) < 100)
                  .map((item) => [item.value, item.title] as [string, string])
              "
              :skip-sort="true"
              v-model:value="user.acl"
              @is-open="fixModalDropdownScroll($event, $refs.accessLevelDropdownRef)"
              ref="accessLevelDropdownRef"
              :label="$t('usersManagement.modal.accessLevel')"
              data-qa="switch-access-level"
              :custom-container="container"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              :disabled="userForm === 'edit'"
              :items="
                unionOrganizationsData.map((item) => [item.id, item.name] as [string, string])
              "
              v-model:value="user.org_id"
              @is-open="fixModalDropdownScroll($event, $refs.organizationDropdownRef)"
              ref="organizationDropdownRef"
              :label="$t('usersManagement.organization')"
              data-qa="switch-organization"
              :custom-container="container"
            />
          </div>
        </template>
      </rbz-modal-dialog>
      <rbz-modal-dialog
        :show="!!deleteModalShown"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteUser"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="deleteModalShown = false"
        @closeModal="deleteModalShown = false"
        size="small"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: user.contact_name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { useBranchesStore } from '@/store/BranchesStore';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { mapStores } from 'pinia';
import { UserDetails, Organization, HttpRequestMethods } from '@/types';
import RequestsUtils from '@/helpers/RequestsUtils';
import _ from 'lodash';
import PageTemplate from '@/views/PageTemplate.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';

export default defineComponent({
  name: 'UserManagement',
  components: {
    RbzFormInput,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
    RbzTable,
  },
  watch: {
    selectedBranch: {
      handler: function (val, oldVal) {
        if ((this.$route.name as string).includes('Users Management') && val && val !== oldVal) {
          this.loadUsers();
        }
      },
      immediate: true,
    },
  },
  data() {
    const accessLevels = [
      {
        title: 'WAAP viewer',
        value: '1',
      },
      {
        title: 'WAAP editor',
        value: '11',
      },
      {
        title: 'WAAP manager',
        value: '20',
      },
      {
        title: 'WAAP system admin',
        value: '100',
      },
    ];
    return {
      columns: [
        {
          id: 'contact-name',
          title: this.$t('usersManagement.usersTable.contactName'),
          fieldNames: ['contact_name'],
          isSortable: true,
          isSearchable: true,
          autoFocus: true,
        },
        {
          id: 'email',
          title: this.$t('usersManagement.email'),
          fieldNames: ['email'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'mobile',
          title: this.$t('usersManagement.mobile'),
          fieldNames: ['mobile'],
          isSortable: true,
          isSearchable: true,
        },
        {
          id: 'access-level',
          title: this.$t('usersManagement.usersTable.accessLevel'),
          fieldNames: ['acl'],
          displayFunction: (item: UserDetails) => {
            const accessLevel = accessLevels.find((accessLevel) => {
              return accessLevel.value === item.acl;
            });
            return accessLevel?.title || '';
          },
          isSortable: true,
          isSearchable: true,
        },
        {
          id: 'organization',
          title: this.$t('usersManagement.organization'),
          fieldNames: ['org_name'],
          isSortable: true,
          isSearchable: true,
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      isLoading: true,
      accessLevels: accessLevels,
      users: [] as Organization[],

      // Modals
      userForm: null as 'new' | 'edit',
      deleteModalShown: false,
      user: null as UserDetails,
      newUserId: '',
      newUserTemplate: {
        contact_name: '',
        email: '',
        mobile: '',
        acl: null,
        org_id: null,
      } as UserDetails,
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore),

    entityName() {
      return {
        plural: this.$t('usersManagement.plural'),
        singular: this.$t('usersManagement.singular'),
      };
    },

    unionUsersData(): UserDetails[] {
      return _.flatMap(this.users, 'users');
    },

    unionUsersEmail(): string[] {
      return _.flatMap(this.unionUsersData, 'email');
    },

    unionOrganizationsData(): { name: string; id: string }[] {
      return _.flatMap(this.users, (item) => {
        return {
          name: item.name,
          id: item.id,
        };
      });
    },

    isNameValid(): boolean {
      const validNamePattern = new RegExp(
        "^([a-zA-Z\xC0-\uFFFF']){1,30}\\s?(?: ([a-zA-Z\xC0-\uFFFF']){1,30}\\s?){0,2}$"
      );
      return validNamePattern.test(this.user?.contact_name);
    },

    isEmailExist(): boolean {
      return this.unionUsersEmail.some((email: string) => {
        return email === this.user?.email;
      });
    },

    isEmailValid(): boolean {
      const validEmailPattern = new RegExp(
        '[-a-zA-Z0-9_.]{1,256}@(?:[-a-zA-Z0-9]+\\.)+[a-zA-Z]{2,}'
      );
      return (
        (this.userForm === 'edit' || !this.isEmailExist) && validEmailPattern.test(this.user?.email)
      );
    },

    isMobileValid(): boolean {
      const validMobilePattern = new RegExp('^\\+[\\d]{7,20}$');
      return this.user?.mobile ? validMobilePattern.test(this.user?.mobile) : true;
    },

    isUserFormValid(): boolean {
      return (
        (this.userForm === 'edit' || !this.isEmailExist) &&
        this.isNameValid &&
        this.isEmailValid &&
        this.isMobileValid &&
        !!this.user.acl &&
        !!this.user.org_id
      );
    },
  },
  methods: {
    getUserById(id: string) {
      return _.cloneDeep(this.unionUsersData?.find((user: UserDetails) => user.id === id));
    },

    openAddNewUserModal() {
      this.user = _.cloneDeep(this.newUserTemplate);
      this.userForm = 'new';
    },

    openEditUserModal(user: UserDetails) {
      this.user = this.getUserById(user.id);
      this.userForm = 'edit';
    },

    openDeleteUserModal(user: UserDetails) {
      this.user = this.getUserById(user.id);
      this.deleteModalShown = true;
    },

    fixModalDropdownScroll(isOpen: boolean, dropdownRef: InstanceType<typeof RbzDropdown>) {
      if (isOpen) {
        setTimeout(() => {
          dropdownRef.dropdownListRef.scrollIntoView();
        }, 0);
      }
    },

    async loadUsers() {
      this.isLoading = true;
      const url = 'accounts/users';
      const methodName = 'GET';
      const response = await RequestsUtils.sendRequest({ methodName, url });
      this.users = response?.data?.items || [];
      this.isLoading = false;
    },

    async saveUser() {
      this.isLoading = true;
      const newUser = this.userForm === 'new';
      let url;
      let method: HttpRequestMethods;
      let successMessage = '';
      let failureMessage = '';
      if (newUser) {
        url = 'accounts/users';
        method = 'POST';
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        url = `accounts/${this.user.id}`;
        method = 'PUT';
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      const response = await RequestsUtils.sendRequest({
        methodName: method,
        url: url,
        data: this.user,
        successMessage,
        failureMessage,
      });
      if (response) {
        if (newUser) {
          this.newUserId = response?.data?.id;
        }
      }
      this.user = null;
      this.userForm = null;
      await this.loadUsers();
    },

    async deleteUser() {
      this.isLoading = true;
      const url = `accounts/${this.user.id}`;
      const method = 'DELETE';
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        methodName: method,
        url: url,
        successMessage,
        failureMessage,
      });
      this.deleteModalShown = false;
      await this.loadUsers();
    },
  },
});
</script>
<style lang="scss" scoped>
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

:deep(.rbz-table .data-row) {
  &.highlight-row {
    background-color: map.get(colors.$colors, 'ice-blue');
  }
}
</style>
